import * as StateLib from "@/Utils/StateLib";
import React, { Fragment, useRef, useMemo } from "react";
import {
  ToolIcon,
  Menu,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Counter,
  ColorTrigger,
  Tooltip,
  Select
} from "@theorgchartdev/storybook";
import CompositeColorPicker from "../../../Blocks/Composites/CompositeColorPicker/CompositeColorPicker";
import { getHoverTipContent } from "@/Utils/Misc";
import {
  BuildVertAlignmentOptions,
  BuildHorzAlignmentOptions,
  BuildFontNameOptions,
  BuildFontSizeOptions,
  BuildCellWrapTextOptions,
  BuildFieldDisplayModeOptions,
  BuildFormualScopeOptions,
  formulaScopes
} from "../ToolbarOptions";
import { encodeColorObject } from "@/appComponents/Blocks/Composites/CompositeColorPicker/ColorPicker.utils";
import { CellFillModeComposite } from "./CellFillModeComposite";
import { CellFieldsMenu } from "@/appComponents/Blocks/Menus/FieldsMenu/CellFieldsMenu";
import { InsertCellField } from "@/appComponents/Blocks/Composites/LayoutHoverToolbar/Utils";

const dividerClass = "ocn-toolbar-horizontal-divider";
import {
  enumUpdateProperty,
  enumChartElement,
  enumFillMode,
  enumBoxSubTypes,
  enumCellContentType,
  kLabelOptions,
  enumObjClassNames
} from "@coreapp/constants";
import { QChartBox } from "@coreapp/CoreApp/QChartBox";
import { QObjDBFieldDef } from "@coreapp/CoreApp/QObjDBFieldDef";
import { Globals } from "@coreapp/CoreApp/Globals";
import { QColorPickerResult } from "@coreapp/CoreApp/QColorPickerResult";
import { appstr } from "@coreapp/utils/appstr";
import { isInBoxLayoutEditor } from "@coreapp/utils/isInBoxLayoutEditor";
import { GetFieldLabel } from "@coreapp/utils/GetFieldLabel";
import { editModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

const DEFAULT_FIELD_PLACEHOLDER = "Insert field";
const FREE_TEXT_KEY = "freetext";
const NO_CONTENT_KEY = "No Content";

const TextFormatToolsComposite = props => {
  const inBackgroundEditMode = Globals._uiConfig.InBackgroundEditorMode();
  const inBoxLayoutEditorMode = Globals._uiConfig.InBoxLayoutEditorMode();
  // InBoxLayoutEditorMode() (route-based) must win over InBackgroundEditorMode() (URL-param-based,
  // ?tool=background persists as a passenger when navigating to /boxlayout/:layoutKey) — otherwise
  // editing a box style that belongs to a background always resolves to the Background id.
  const modeTestId = (suffix, editSuffix = suffix) =>
    inBoxLayoutEditorMode
      ? `box-layout-editor-toolbar-${suffix}`
      : inBackgroundEditMode
        ? `background-editor-toolbar-${suffix}`
        : editModeTestId(editSuffix);
  const modePrefixId = (suffix, plainSuffix = suffix) =>
    inBoxLayoutEditorMode
      ? `box-layout-editor-${suffix}`
      : inBackgroundEditMode
        ? `background-editor-${suffix}`
        : plainSuffix;
  const fontSizeOpenCtrlRef = useRef(null);
  const fontColorOpenCtrlRef = useRef(null);
  const fillColorOpenCtrlRef = useRef(null);
  const horzAlignOpenCtrlRef = useRef(null);
  const vertAlignOpenCtrlRef = useRef(null);
  const wrapOpenCtrlRef = useRef(null);

  let wsMgr = StateLib.GetWSMgr();
  let fontName;
  let fontSize;
  let activeFontFormats = [];
  let horzAlignment;
  let vertAlignment;
  let fontColor;
  let cellFillMode;
  let cellFillColor;
  let cellFillGradientColor;
  let wrapText;
  let fieldDisplayMode = 0;
  let fieldRefName = DEFAULT_FIELD_PLACEHOLDER;
  let firstSelectedCell = null;
  let fieldRef = null;
  let formulaScope;
  let boxSubType;
  let isFreeText = false;
  let isBlankCell = false;
  const showFieldDisplay = props.showFieldDisplay ?? false;
  if (wsMgr) {
    firstSelectedCell = wsMgr.GetFirstSelectedElement(enumChartElement.boxCell);
    fontName = wsMgr.SurveySelection(enumUpdateProperty.fontName);
    fontSize = wsMgr.SurveySelection(enumUpdateProperty.fontSize);
    if (wsMgr.SurveySelection(enumUpdateProperty.cellTextBold)) activeFontFormats.push("bold");
    if (wsMgr.SurveySelection(enumUpdateProperty.cellTextItalic)) activeFontFormats.push("italic");
    if (wsMgr.SurveySelection(enumUpdateProperty.cellTextUnderline))
      activeFontFormats.push("underline");
    horzAlignment = wsMgr.SurveySelection(enumUpdateProperty.cellTextAlign);
    vertAlignment = wsMgr.SurveySelection(enumUpdateProperty.cellTextVAlign);
    let fontColorCP = wsMgr.SurveySelection(enumUpdateProperty.cellFontColor);
    if (fontColorCP) fontColor = fontColorCP.fSelectedColor;
    let cellFillColorCP = wsMgr.SurveySelection(enumUpdateProperty.cellFillColor);
    if (cellFillColorCP) {
      cellFillMode = cellFillColorCP.fFillMode;
      switch (cellFillColorCP.fFillMode) {
        case enumFillMode.noFill:
          cellFillColor = null;
          break;
        case enumFillMode.linearGradientDown:
          cellFillGradientColor = cellFillColorCP.fSelectedColor2;
        case enumFillMode.solidFill:
        case enumFillMode.separator:
        case enumFillMode.dashline:
        case enumFillMode.triangle:
        case enumFillMode.lefttriangle:
          cellFillColor = cellFillColorCP.fSelectedColor;
          break;
      }
    }

    var anyElem = wsMgr.GetFirstSelectedElement();
    if (firstSelectedCell) {
      var parentBox = firstSelectedCell.GetParentBox();
      if (parentBox) boxSubType = parentBox.GetBoxSubType();
    } else if (anyElem && anyElem instanceof QChartBox) {
      boxSubType = anyElem.GetBoxSubType();
    }

    formulaScope = wsMgr.SurveySelection(enumUpdateProperty.cellRefType);
    wrapText = wsMgr.SurveySelection(enumUpdateProperty.cellWrapText);
    fieldDisplayMode = wsMgr.SurveySelection(enumUpdateProperty.fieldDisplayMode);
    if (firstSelectedCell) {
      const cellDesc = firstSelectedCell.GetCellDesc();
      const cellType = cellDesc?.GetCellContentType();
      isBlankCell = cellType === enumCellContentType.Blank;
      isFreeText = firstSelectedCell.IsFreeText();
      fieldRef = firstSelectedCell.GetField();

      if (isBlankCell) {
        fieldRefName = NO_CONTENT_KEY;
      } else if (isFreeText) {
        fieldRefName = FREE_TEXT_KEY;
      } else if (fieldRef) {
        fieldRefName = fieldRef instanceof QObjDBFieldDef ? fieldRef.GetFieldName() : fieldRef;
      }
    }
  }

  const textVAlignOptions = BuildVertAlignmentOptions(UpdateTextVAlignment);
  const textAlignOptions = BuildHorzAlignmentOptions(UpdateTextHAlignment);
  const fontNameOptions = BuildFontNameOptions(UpdateFontName);
  const fontSizeOptions = BuildFontSizeOptions(UpdateFontSize);
  const fontFormats = ["bold", "underline", "italic"];
  const cellWrapTextOptions = BuildCellWrapTextOptions(UpdateCellWrapText);
  const fieldDisplayModeOptions = BuildFieldDisplayModeOptions(UpdateFieldDisplayMode);
  const formulaScopeOptions = BuildFormualScopeOptions(UpdateFormulaScope);
  const formulaScopeTestId = modePrefixId("ctx-toolbar-formula-scope");
  const showScopeTools = [
    enumBoxSubTypes.refBoxTable,
    enumBoxSubTypes.refChart,
    enumBoxSubTypes.enumeratedTable,
    enumBoxSubTypes.refBoxLegend
  ].includes(boxSubType);

  const hideTextControls = props?.hideTextControls;

  const textVAlignTrigger = (
    <ToolIcon
      name={"line-height"}
      title={appstr("Vertical align")}
      tooltipProps={{
        ...getHoverTipContent("Vertical alignment"),
        referenceOpenCtrlRef: vertAlignOpenCtrlRef
      }}
    />
  );
  const textHAlignTrigger = (
    <ToolIcon
      name={"align-left"}
      title={appstr("Text align")}
      tooltipProps={{
        ...getHoverTipContent("Horizontal alignment"),
        referenceOpenCtrlRef: horzAlignOpenCtrlRef
      }}
    />
  );
  const wrapTrigger = (
    <ToolIcon
      name={"arrow-turn-down-left"}
      title={appstr("Wrap text")}
      tooltipProps={{
        ...getHoverTipContent("wraptext"),
        referenceOpenCtrlRef: wrapOpenCtrlRef
      }}
    />
  );

  const showFieldMenus = showFieldDisplay && firstSelectedCell;
  const Component = props?.useSelect ? Select : Menu;

  const [selectedCellField, selectedCellHierarchyValues] = useMemo(() => {
    let field = "none";
    let hierarchyValues = [];
    if (firstSelectedCell) {
      const cell = firstSelectedCell.GetCellDesc();
      const cellType = cell.GetCellContentType();
      switch (cellType) {
        case enumCellContentType.Blank:
          field = "none";
          hierarchyValues = ["none"];
          break;
        case enumCellContentType.FreeText:
          field = "freetext";
          hierarchyValues = ["freetext"];
          break;
        case enumCellContentType.Field:
        case enumCellContentType.object:
        case enumCellContentType.pieChart:
          const FIELD = "field";
          if (props?.enableMultiFieldMenu) {
            const objClassName = fieldRef?.GetFieldClassName();
            field = `${objClassName}-${fieldRef?.GetFieldName()}`;
            hierarchyValues = [
              field,
              objClassName === enumObjClassNames.position ? "positionFields" : "employeeFields",
              FIELD
            ];
          } else {
            field = fieldRef?.GetFieldID();
            hierarchyValues = [fieldRef?.GetFieldID(), FIELD];
          }
          break;
        default:
          break;
      }
    }
    return [field, hierarchyValues];
  }, [firstSelectedCell, fieldRef, props?.enableMultiFieldMenu]);

  const fieldListProps = useMemo(
    () => ({
      value: selectedCellHierarchyValues,
      ...props?.menuProps
    }),
    [selectedCellHierarchyValues, props?.menuProps]
  );

  return (
    <Fragment>
      {showFieldMenus && (
        <>
          <CellFieldsMenu
            useSelect={props?.useSelect}
            enableMultiFieldMenu={props?.enableMultiFieldMenu}
            label={
              fieldRefName === DEFAULT_FIELD_PLACEHOLDER || isFreeText || isBlankCell
                ? appstr(fieldRefName)
                : fieldRefName
            }
            onClick={option => {
              const fieldDef = option.eventInfo?.fieldDef;
              if (props.enableMultiFieldMenu && fieldDef) {
                InsertCellField({ ...option, ...(fieldDef ? { fieldDef } : {}) });
              } else {
                InsertCellField(option);
              }
            }}
            dataTestIdPrefix={props?.dataTestIdPrefix ?? undefined}
            fieldListProps={fieldListProps}
            includeOptions={{
              calcs: true,
              internal: isInBoxLayoutEditor(),
              photos: !Globals._uiConfig.InBackgroundEditorMode(),
              locked: false,
              none: true,
              freetext: true
            }}
            value={selectedCellField}
            {...props?.menuProps}
          />
          <Component
            dataTestId={modeTestId("fields-display-menu", "text-format-fields-display-menu")}
            buttonSize={"small"}
            label={appstr(kLabelOptions[fieldDisplayMode ?? 0])}
            options={fieldDisplayModeOptions}
            tooltipProps={getHoverTipContent("Field label")}
            triggerProps={{ tooltipProps: { ...getHoverTipContent("Field label") } }}
            value={fieldDisplayMode ?? 0}
            {...props?.menuProps}
          />
        </>
      )}
      {!hideTextControls && (
        <Fragment>
          {showScopeTools && (
            <Menu
              dataTestId={formulaScopeTestId}
              popoverProps={{
                floatingProps: { "data-testid": `${formulaScopeTestId}-popover` }
              }}
              buttonSize={"small"}
              label={appstr(formulaScopes[formulaScope ?? 0].label)}
              options={formulaScopeOptions}
              triggerProps={{
                tooltipProps: {
                  ...getHoverTipContent(formulaScopes[formulaScope ?? 0].hover)
                }
              }}
              value={formulaScope ?? 0}
            />
          )}
          {(showFieldMenus || showScopeTools) && (
            <div className={dividerClass}>
              <Divider vertical={true} />
            </div>
          )}
          <Menu
            dataTestId={modeTestId("font-name-menu")}
            buttonSize={"small"}
            label={fontName}
            value={fontName}
            options={fontNameOptions}
            triggerProps={{
              tooltipProps: getHoverTipContent("fontselector")
            }}
          />
          <div className={dividerClass}>
            <Divider vertical={true} />
          </div>
          <Tooltip {...getHoverTipContent("fontsize")} referenceOpenCtrlRef={fontSizeOpenCtrlRef}>
            <Counter
              dataTestIdPrefix={modePrefixId("ctx-toolbar-font-size")}
              maxValue={72}
              openCtrlRef={fontSizeOpenCtrlRef}
              onChange={UpdateFontSize}
              menuOptions={fontSizeOptions}
              currentValue={fontSize}
              defaultValue={11}
              size={"medium"}
            />
          </Tooltip>
          <div className={dividerClass}>
            <Divider vertical={true} />
          </div>
          <ToggleButtonGroup
            value={activeFontFormats}
            onChange={selectedFormats => UpdateFontFormat(selectedFormats, activeFontFormats)}
            exclusive={false}
            enforceValueSet={false}
            spacing={12}
          >
            {fontFormats.map(item => (
              <ToggleButton
                key={item}
                icon={item}
                value={item}
                tooltipProps={getHoverTipContent(item)}
                data-testid={modeTestId(`font-format-${item}-btn`)}
              />
            ))}
          </ToggleButtonGroup>
          <CompositeColorPicker
            openCtrlRef={fontColorOpenCtrlRef}
            dataTestId={modeTestId("font-color")}
            trigger={
              <ColorTrigger
                iconName="font"
                size={"small"}
                tooltipProps={{
                  ...getHoverTipContent("fontcolor"),
                  referenceOpenCtrlRef: fontColorOpenCtrlRef
                }}
              />
            }
            onChange={UpdateFontColor}
            currentColor={fontColor}
            showNoFill={false}
          />
          <div className={dividerClass}>
            <Divider vertical={true} />
          </div>
          <CompositeColorPicker
            openCtrlRef={fillColorOpenCtrlRef}
            dataTestId={modeTestId("cell-fill-color")}
            trigger={
              <ColorTrigger
                iconName="droplet"
                size={"small"}
                tooltipProps={{
                  ...getHoverTipContent("cellfillcolor"),
                  referenceOpenCtrlRef: fillColorOpenCtrlRef
                }}
              />
            }
            showGradient
            onChange={UpdateCellFillColor(cellFillMode)}
            currentColor={encodeColorObject(cellFillColor, cellFillGradientColor)}
          />
          <CellFillModeComposite
            dataTestId={
              inBackgroundEditMode || inBoxLayoutEditorMode
                ? modeTestId("cell-fill-mode")
                : undefined
            }
          />
          <div className={dividerClass}>
            <Divider vertical={true} />
          </div>
          <Menu
            dataTestId={modeTestId("text-halign-menu")}
            openCtrlRef={horzAlignOpenCtrlRef}
            trigger={textHAlignTrigger}
            options={textAlignOptions}
            value={horzAlignment}
          />
          <Menu
            dataTestId={modeTestId("text-valign-menu")}
            openCtrlRef={vertAlignOpenCtrlRef}
            trigger={textVAlignTrigger}
            options={textVAlignOptions}
            value={vertAlignment}
          />
          <Menu
            dataTestId={modeTestId("wrap-text-menu")}
            openCtrlRef={wrapOpenCtrlRef}
            trigger={wrapTrigger}
            options={cellWrapTextOptions}
            value={wrapText}
          />
        </Fragment>
      )}
    </Fragment>
  );
};

const checkFontFormatChanged = (selected, current, key) => {
  return (
    (current.includes(key) && !selected.includes(key)) ||
    (!current.includes(key) && selected.includes(key))
  );
};

const UpdateFontFormat = (selected, active) => {
  if (checkFontFormatChanged(selected, active, "bold"))
    StateLib.UpdateBoxProperty(enumUpdateProperty.cellTextBold, selected.includes("bold"));
  if (checkFontFormatChanged(selected, active, "italic"))
    StateLib.UpdateBoxProperty(enumUpdateProperty.cellTextItalic, selected.includes("italic"));
  if (checkFontFormatChanged(selected, active, "underline"))
    StateLib.UpdateBoxProperty(
      enumUpdateProperty.cellTextUnderline,
      selected.includes("underline")
    );
};

const UpdateTextHAlignment = target => {
  const newHAlignment = target.value;
  StateLib.UpdateBoxProperty(enumUpdateProperty.cellTextAlign, newHAlignment);
};

const UpdateFontSize = newFontSize => {
  StateLib.UpdateBoxProperty(enumUpdateProperty.fontSize, newFontSize);
};

const UpdateTextVAlignment = target => {
  const newVAlignment = target.value;
  StateLib.UpdateBoxProperty(enumUpdateProperty.cellTextVAlign, newVAlignment);
};

const UpdateCellWrapText = target => {
  const newWrapText = target.value;
  StateLib.UpdateBoxProperty(enumUpdateProperty.cellWrapText, newWrapText);
};

const UpdateFieldDisplayMode = target => {
  const newFieldDisplayMode = target.value;
  StateLib.UpdateBoxProperty(enumUpdateProperty.fieldDisplayMode, newFieldDisplayMode);
};

const UpdateFontName = target => {
  const newBorderWeight = target.value;
  StateLib.UpdateBoxProperty(enumUpdateProperty.fontName, newBorderWeight);
};
const UpdateFontColor = selectedFontColor => {
  let color = new QColorPickerResult();
  color.fSelectedColor = selectedFontColor;
  StateLib.UpdateBoxProperty(enumUpdateProperty.cellFontColor, color);
};

const UpdateCellFillColor = mode => selectedColor => {
  let color = new QColorPickerResult();
  color.fFillMode = mode;
  color.fSelectedColor = selectedColor;

  if (Array.isArray(selectedColor)) {
    color.fFillMode = enumFillMode.linearGradientDown;
    color.fSelectedColor = selectedColor[0];
    color.fSelectedColor2 = selectedColor[1];
  } else if (selectedColor) {
    if ([enumFillMode.linearGradientDown, enumFillMode.noFill].includes(color.fFillMode)) {
      color.fFillMode = enumFillMode.solidFill;
    }
  } else {
    color.fFillMode = enumFillMode.noFill;
  }

  StateLib.UpdateBoxProperty(enumUpdateProperty.cellFillColor, color);
};

const UpdateFormulaScope = newFormulaScope => {
  StateLib.UpdateBoxProperty(enumUpdateProperty.cellRefType, newFormulaScope.value);
};

export default TextFormatToolsComposite;
